import React, { useState, useEffect, useRef } from "react";
import citiesData from "../../../../assets/cities.json";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import { UiModal } from "../../../../Components/Shared/UiModal";

function CityModal({ isOpen, onClose, onSubmit, editCity, zones }) {
  const [cityName, setCityName] = useState("");
  const [cityName_ur, setCityNameUr] = useState("");
  const [nameError, setNameError] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [zoneError, setZoneError] = useState("");
  const [inStation, setInStation] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setCityName("");
      setCityNameUr("");
      setNameError("");
      setZoneId("");
      setZoneError("");
      setInStation(false);
      setSearchTerm("");
      setIsDropdownOpen(false);
      return;
    }

    if (editCity) {
      setCityName(editCity.name);
      setCityNameUr(editCity.cityName_ur || "");
      setNameError("");
      setZoneId(editCity.zone_id || "");
      setZoneError("");
      setInStation(editCity.is_instation || false);
      setSearchTerm(editCity.name);
    } else {
      setCityName("");
      setCityNameUr("");
      setNameError("");
      setZoneId("");
      setZoneError("");
      setInStation(false);
      setSearchTerm("");
      setIsDropdownOpen(false);
    }
  }, [isOpen, editCity]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredCities = citiesData.filter(
    (city) =>
      city.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !city.disabled &&
      city.value !== "Other",
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    setNameError("");
    setZoneError("");

    if (!cityName) {
      setNameError("City selection is required");
      return;
    }

    if (!inStation && !zoneId) {
      setZoneError("Zone selection is required when In Station is false");
      return;
    }

    onSubmit(cityName, editCity?.id, zoneId, inStation, cityName_ur);
    setCityName("");
    setCityNameUr("");
    setNameError("");
    setZoneId("");
    setZoneError("");
    setInStation(false);
    setSearchTerm("");
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsDropdownOpen(true);

    if (nameError && value) {
      setNameError("");
    }
  };

  const handleCitySelect = (city) => {
    setCityName(city.value);
    setSearchTerm(city.label);
    setIsDropdownOpen(false);

    if (nameError) {
      setNameError("");
    }
  };

  const handleZoneChange = (e) => {
    const value = e.target.value;
    setZoneId(value);

    if (zoneError && value) {
      setZoneError("");
    }
  };

  const handleInStationChange = (e) => {
    const value = e.target.value === "true";
    setInStation(value);

    if (value) {
      setZoneId("");
      setZoneError("");
    }
  };

  const isFormValid = cityName && (inStation || zoneId);
  const isEditMode = Boolean(editCity);

  return (
    <UiModal
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Delivery Management"
      title={isEditMode ? "Edit City" : "Add City"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            className="ui-btn-cancel min-w-[108px]"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid}
            className="ui-btn-save min-w-[108px]"
          >
            {isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">City details</h4>
        <div className="space-y-4">
          <div>
            <label className="ui-form-label">
              City Name <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={dropdownRef}>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder="Search for a city..."
                className={`ui-input-field ${
                  nameError ? "border-brand-error" : ""
                }`}
                ref={searchInputRef}
              />

              {isDropdownOpen ? (
                <div className="absolute z-20 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg max-h-60">
                  {filteredCities.length > 0 ? (
                    filteredCities.map((city, index) => (
                      <div
                        key={index}
                        onClick={() => handleCitySelect(city)}
                        className={`cursor-pointer px-3 py-2 hover:bg-gray-100 ${
                          city.value === cityName
                            ? "bg-blue-50 text-blue-600"
                            : ""
                        }`}
                      >
                        {city.label}
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-gray-500">
                      No cities found
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {nameError ? <p className="ui-form-error">{nameError}</p> : null}
            <UrduInput
              label="شہر کا نام"
              name="cityName_ur"
              value={cityName_ur}
              onChange={(e) => setCityNameUr(e.target.value)}
            />
          </div>

          <div>
            <label className="ui-form-label">In Station</label>
            <select
              value={inStation.toString()}
              onChange={handleInStationChange}
              className="ui-select-field"
            >
              <option value="false">False</option>
              <option value="true">True</option>
            </select>
          </div>

          {!inStation ? (
            <div>
              <label className="ui-form-label">
                Zone Name <span className="text-red-500">*</span>
              </label>
              <select
                value={zoneId}
                onChange={handleZoneChange}
                className={`ui-select-field ${
                  zoneError ? "border-brand-error" : ""
                }`}
              >
                <option value="">Select a zone</option>
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.zone_name}
                  </option>
                ))}
              </select>
              {zoneError ? <p className="ui-form-error">{zoneError}</p> : null}
            </div>
          ) : null}
        </div>
      </section>
    </UiModal>
  );
}

export default CityModal;
